/**
 * Premium Markdown Parser V2 for Chatbot UI
 * 
 * Enhanced version with:
 * - Streaming buffer support
 * - Numbered lists
 * - Nested inline formatting
 * - Callout blocks (> [!NOTE])
 * - Collection cards (:::collect:)
 * - Emoji prefix detection
 */

// ============================================================================
// Types
// ============================================================================

export type BlockType =
    | 'paragraph'
    | 'heading'
    | 'table'
    | 'list'
    | 'fenced'
    | 'code'
    | 'blockquote'
    | 'callout'
    | 'collection'
    | 'cta'
    | 'hr';

export type SemanticHint =
    | 'property-card'
    | 'timeline'
    | 'assessment'
    | 'comparison'
    | 'steps'
    | 'metrics'
    | 'recommendation'
    | 'warning'
    | 'info'
    | 'note'
    | 'tip'
    | 'caution';

export type CollectionType = 'tags' | 'choice' | 'scale' | 'input';

export type CTABlockType = 'discovery' | 'architecture' | 'contact' | 'quote' | 'book';

export interface CTAData {
    ctaType: CTABlockType;
    label: string;
    description?: string;
    action: string;
    url: string;
    price?: string;
    collectContact?: boolean;
}

export interface InlineToken {
    type: 'text' | 'bold' | 'italic' | 'code' | 'link' | 'emoji';
    content: string;
    href?: string;
    children?: InlineToken[]; // For nested formatting
}

export interface TableRow {
    cells: string[];
    isHeader?: boolean;
}

export interface CollectionData {
    collectionType: CollectionType;
    label: string;
    options?: string[];
    placeholder?: string;
}

export interface ParsedBlock {
    type: BlockType;
    hint?: SemanticHint;
    level?: number; // For headings (1-6)
    content?: string;
    inline?: InlineToken[];
    items?: ParsedBlock[]; // For lists
    rows?: TableRow[]; // For tables
    language?: string; // For code blocks
    ordered?: boolean; // For lists
    calloutType?: 'note' | 'tip' | 'important' | 'warning' | 'caution';
    collection?: CollectionData; // For collection cards
    cta?: CTAData; // For CTA lead generation cards
    emojiPrefix?: string; // Detected emoji at start
    raw?: string; // Original text for fallback
}

// ============================================================================
// Streaming Buffer
// ============================================================================

export interface StreamBuffer {
    buffer: string;
    completedBlocks: ParsedBlock[];
    isInOpenBlock: boolean;
    openBlockType?: 'code' | 'fenced' | 'collection' | 'table';
    openBlockContent: string[];
    openBlockMeta?: string;
}

export function createStreamBuffer(): StreamBuffer {
    return {
        buffer: '',
        completedBlocks: [],
        isInOpenBlock: false,
        openBlockContent: [],
    };
}

export function appendToStream(chunk: string, state: StreamBuffer): {
    newBlocks: ParsedBlock[];
    state: StreamBuffer;
} {
    const combined = state.buffer + chunk;
    const lines = combined.split('\n');

    // Keep the last line as buffer (might be incomplete)
    const incompleteLastLine = !combined.endsWith('\n');
    const buffer = incompleteLastLine ? lines.pop() || '' : '';

    const newBlocks: ParsedBlock[] = [];

    for (const line of lines) {
        const result = processStreamLine(line, state);
        if (result.block) {
            newBlocks.push(result.block);
        }
        state = result.state;
    }

    return {
        newBlocks,
        state: { ...state, buffer }
    };
}

function processStreamLine(line: string, state: StreamBuffer): {
    block?: ParsedBlock;
    state: StreamBuffer;
} {
    const trimmed = line.trim();

    // Handle open blocks
    if (state.isInOpenBlock) {
        // Check for closing delimiter
        if (
            (state.openBlockType === 'code' && /^```\s*$/.test(trimmed)) ||
            (state.openBlockType === 'fenced' && /^:::\s*$/.test(trimmed)) ||
            (state.openBlockType === 'collection' && /^:::\s*$/.test(trimmed))
        ) {
            const content = state.openBlockContent.join('\n');
            let block: ParsedBlock;

            if (state.openBlockType === 'code') {
                block = {
                    type: 'code',
                    language: state.openBlockMeta,
                    content,
                    raw: `\`\`\`${state.openBlockMeta || ''}\n${content}\n\`\`\``,
                };
            } else if (state.openBlockType === 'collection') {
                // Check if this is actually a CTA block
                if (state.openBlockMeta?.startsWith('cta:')) {
                    const ctaType = state.openBlockMeta.replace('cta:', '');
                    block = parseCTABlock(ctaType, content);
                } else {
                    block = parseCollectionBlock(state.openBlockMeta || '', content);
                }
            } else {
                const innerBlocks = parseMarkdown(content);
                block = {
                    type: 'fenced',
                    hint: state.openBlockMeta as SemanticHint,
                    items: innerBlocks,
                    content,
                    raw: `:::${state.openBlockMeta}\n${content}\n:::`,
                };
            }

            return {
                block,
                state: {
                    ...state,
                    isInOpenBlock: false,
                    openBlockType: undefined,
                    openBlockContent: [],
                    openBlockMeta: undefined,
                },
            };
        }

        // Still inside open block
        return {
            state: {
                ...state,
                openBlockContent: [...state.openBlockContent, line],
            },
        };
    }

    // Check for block openers
    const codeMatch = /^```(\w*)\s*$/.exec(trimmed);
    if (codeMatch) {
        return {
            state: {
                ...state,
                isInOpenBlock: true,
                openBlockType: 'code',
                openBlockMeta: codeMatch[1] || undefined,
                openBlockContent: [],
            },
        };
    }

    const ctaMatch = /^:::cta:(\w+)\s*$/.exec(trimmed);
    if (ctaMatch) {
        return {
            state: {
                ...state,
                isInOpenBlock: true,
                openBlockType: 'collection', // Reuse collection handler for simplicity
                openBlockMeta: `cta:${ctaMatch[1]}`,
                openBlockContent: [],
            },
        };
    }

    const collectionMatch = /^:::collect:(\w+)\s*$/.exec(trimmed);
    if (collectionMatch) {
        return {
            state: {
                ...state,
                isInOpenBlock: true,
                openBlockType: 'collection',
                openBlockMeta: collectionMatch[1],
                openBlockContent: [],
            },
        };
    }

    const fencedMatch = /^:::([a-z-]+)\s*$/.exec(trimmed);
    if (fencedMatch) {
        return {
            state: {
                ...state,
                isInOpenBlock: true,
                openBlockType: 'fenced',
                openBlockMeta: fencedMatch[1],
                openBlockContent: [],
            },
        };
    }

    // Parse single-line blocks
    const block = parseSingleLine(line);
    return { block: block || undefined, state };
}

// ============================================================================
// Regex Patterns
// ============================================================================

const PATTERNS = {
    // Block-level patterns
    heading: /^(#{1,6})\s+(.+)$/,
    hr: /^(?:---|\*\*\*|___)\s*$/,
    codeBlockStart: /^```(\w*)\s*$/,
    codeBlockEnd: /^```\s*$/,
    fencedStart: /^:::([a-z-]+)\s*$/,
    fencedEnd: /^:::\s*$/,
    collectionStart: /^:::collect:(\w+)\s*$/,
    ctaStart: /^:::cta:(\w+)\s*$/,
    callout: /^>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*$/i,
    blockquote: /^>\s*(.*)$/,
    unorderedList: /^[\s]*[-*•]\s+(.+)$/,
    orderedList: /^[\s]*(\d+)\.\s+(.+)$/,
    tableRow: /^\|(.+)\|$/,
    tableSeparator: /^\|[\s\-:|]+\|$/,

    // Emoji prefix detection
    emojiPrefix: /^([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}])\s*/u,

    // Inline patterns - order matters for nested
    boldCode: /\*\*([^*]*`[^`]+`[^*]*)\*\*/g, // **text with `code`**
    bold: /\*\*([^*]+)\*\*/g,
    italic: /(?<!\*)\*([^*]+)\*(?!\*)/g,
    inlineCode: /`([^`]+)`/g,
    link: /\[([^\]]+)\]\(([^)]+)\)/g,
};

// ============================================================================
// Collection Card Parser
// ============================================================================

function parseCollectionBlock(type: string, content: string): ParsedBlock {
    const lines = content.split('\n');
    let label = '';
    let options: string[] = [];
    let placeholder = '';

    for (const line of lines) {
        const trimmed = line.trim();
        const labelMatch = /^label:\s*(.+)$/.exec(trimmed);
        const optionsMatch = /^options:\s*\[(.+)\]$/.exec(trimmed);
        const placeholderMatch = /^placeholder:\s*(.+)$/.exec(trimmed);

        if (labelMatch) label = labelMatch[1];
        if (optionsMatch) options = optionsMatch[1].split(',').map(o => o.trim());
        if (placeholderMatch) placeholder = placeholderMatch[1];
    }

    return {
        type: 'collection',
        collection: {
            collectionType: type as CollectionType,
            label,
            options: options.length > 0 ? options : undefined,
            placeholder: placeholder || undefined,
        },
        raw: `:::collect:${type}\n${content}\n:::`,
    };
}

// ============================================================================
// CTA Block Parser (Lead Generation)
// ============================================================================

function parseCTABlock(type: string, content: string): ParsedBlock {
    const lines = content.split('\n');
    let label = '';
    let description = '';
    let action = 'Learn More';
    let url = '/contact';
    let price = '';
    let collectContact = false;

    for (const line of lines) {
        const trimmed = line.trim();
        const labelMatch = /^label:\s*(.+)$/.exec(trimmed);
        const descMatch = /^description:\s*(.+)$/.exec(trimmed);
        const actionMatch = /^action:\s*(.+)$/.exec(trimmed);
        const urlMatch = /^url:\s*(.+)$/.exec(trimmed);
        const priceMatch = /^price:\s*(.+)$/.exec(trimmed);
        const collectMatch = /^collect:\s*(\w+)$/.exec(trimmed);

        if (labelMatch) label = labelMatch[1];
        if (descMatch) description = descMatch[1];
        if (actionMatch) action = actionMatch[1];
        if (urlMatch) url = urlMatch[1];
        if (priceMatch) price = priceMatch[1];
        if (collectMatch) collectContact = true;
    }

    return {
        type: 'cta',
        cta: {
            ctaType: type as CTABlockType,
            label,
            description: description || undefined,
            action,
            url,
            price: price || undefined,
            collectContact,
        },
        raw: `:::cta:${type}\n${content}\n:::`,
    };
}

// ============================================================================
// Inline Parser (with nesting support)
// ============================================================================

export function parseInline(text: string): InlineToken[] {
    const tokens: InlineToken[] = [];

    // Process in priority order to handle nesting
    const _remaining = text;
    let lastIndex = 0;

    // Combined pattern for all inline elements
    const combined = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*[^*]+\*)/g;

    let match;
    while ((match = combined.exec(text)) !== null) {
        // Add preceding text
        if (match.index > lastIndex) {
            const preceding = text.slice(lastIndex, match.index);
            if (preceding) {
                tokens.push(...parseTextWithEmoji(preceding));
            }
        }

        const matched = match[0];

        if (matched.startsWith('**')) {
            // Check for nested code in bold
            const innerContent = matched.slice(2, -2);
            if (innerContent.includes('`')) {
                tokens.push({
                    type: 'bold',
                    content: innerContent,
                    children: parseInline(innerContent)
                });
            } else {
                tokens.push({ type: 'bold', content: innerContent });
            }
        } else if (matched.startsWith('`')) {
            tokens.push({ type: 'code', content: matched.slice(1, -1) });
        } else if (matched.startsWith('[')) {
            const linkMatch = /\[([^\]]+)\]\(([^)]+)\)/.exec(matched);
            if (linkMatch) {
                tokens.push({
                    type: 'link',
                    content: linkMatch[1],
                    href: linkMatch[2]
                });
            }
        } else if (matched.startsWith('*')) {
            tokens.push({ type: 'italic', content: matched.slice(1, -1) });
        }

        lastIndex = match.index + matched.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        tokens.push(...parseTextWithEmoji(text.slice(lastIndex)));
    }

    // If no tokens, return whole text
    if (tokens.length === 0 && text.trim()) {
        tokens.push(...parseTextWithEmoji(text));
    }

    return tokens;
}

function parseTextWithEmoji(text: string): InlineToken[] {
    const tokens: InlineToken[] = [];
    const emojiMatch = PATTERNS.emojiPrefix.exec(text);

    if (emojiMatch) {
        tokens.push({ type: 'emoji', content: emojiMatch[1] });
        const rest = text.slice(emojiMatch[0].length);
        if (rest) {
            tokens.push({ type: 'text', content: rest });
        }
    } else if (text) {
        tokens.push({ type: 'text', content: text });
    }

    return tokens;
}

// ============================================================================
// Single Line Parser
// ============================================================================

function parseSingleLine(line: string): ParsedBlock | null {
    const trimmed = line.trim();

    if (!trimmed) return null;

    // Horizontal rule
    if (PATTERNS.hr.test(trimmed)) {
        return { type: 'hr', raw: line };
    }

    // Heading
    const headingMatch = PATTERNS.heading.exec(trimmed);
    if (headingMatch) {
        const content = headingMatch[2];
        const emojiMatch = PATTERNS.emojiPrefix.exec(content);

        return {
            type: 'heading',
            level: headingMatch[1].length,
            content,
            inline: parseInline(content),
            emojiPrefix: emojiMatch?.[1],
            raw: line,
        };
    }

    // Callout start (> [!NOTE])
    const calloutMatch = PATTERNS.callout.exec(trimmed);
    if (calloutMatch) {
        return {
            type: 'callout',
            calloutType: calloutMatch[1].toLowerCase() as ParsedBlock['calloutType'],
            content: '',
            raw: line,
        };
    }

    // Regular blockquote
    const quoteMatch = PATTERNS.blockquote.exec(trimmed);
    if (quoteMatch && !PATTERNS.callout.test(trimmed)) {
        return {
            type: 'blockquote',
            content: quoteMatch[1],
            inline: parseInline(quoteMatch[1]),
            raw: line,
        };
    }

    // Default: paragraph
    const emojiMatch = PATTERNS.emojiPrefix.exec(trimmed);
    return {
        type: 'paragraph',
        content: trimmed,
        inline: parseInline(trimmed),
        emojiPrefix: emojiMatch?.[1],
        raw: line,
    };
}

// ============================================================================
// Block Parser (Full Document)
// ============================================================================

export function parseMarkdown(markdown: string): ParsedBlock[] {
    const lines = markdown.split('\n');
    const blocks: ParsedBlock[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // Skip empty lines
        if (!trimmed) {
            i++;
            continue;
        }

        // Horizontal rule
        if (PATTERNS.hr.test(trimmed)) {
            blocks.push({ type: 'hr', raw: line });
            i++;
            continue;
        }

        // Heading
        const headingMatch = PATTERNS.heading.exec(trimmed);
        if (headingMatch) {
            const content = headingMatch[2];
            const emojiMatch = PATTERNS.emojiPrefix.exec(content);

            blocks.push({
                type: 'heading',
                level: headingMatch[1].length,
                content,
                inline: parseInline(content),
                emojiPrefix: emojiMatch?.[1],
                raw: line,
            });
            i++;
            continue;
        }

        // CTA block (:::cta:type) - Lead Generation
        const ctaMatch = PATTERNS.ctaStart.exec(trimmed);
        if (ctaMatch) {
            const ctaType = ctaMatch[1];
            const contentLines: string[] = [];
            i++;

            while (i < lines.length && !PATTERNS.fencedEnd.test(lines[i].trim())) {
                contentLines.push(lines[i]);
                i++;
            }

            blocks.push(parseCTABlock(ctaType, contentLines.join('\n')));
            i++;
            continue;
        }

        // Collection block (:::collect:type)
        const collectionMatch = PATTERNS.collectionStart.exec(trimmed);
        if (collectionMatch) {
            const collectionType = collectionMatch[1];
            const contentLines: string[] = [];
            i++;

            while (i < lines.length && !PATTERNS.fencedEnd.test(lines[i].trim())) {
                contentLines.push(lines[i]);
                i++;
            }

            blocks.push(parseCollectionBlock(collectionType, contentLines.join('\n')));
            i++;
            continue;
        }

        // Fenced block (:::type)
        const fencedMatch = PATTERNS.fencedStart.exec(trimmed);
        if (fencedMatch) {
            const hint = fencedMatch[1] as SemanticHint;
            const contentLines: string[] = [];
            i++;

            while (i < lines.length && !PATTERNS.fencedEnd.test(lines[i].trim())) {
                contentLines.push(lines[i]);
                i++;
            }

            const innerContent = contentLines.join('\n');
            const innerBlocks = parseMarkdown(innerContent);

            blocks.push({
                type: 'fenced',
                hint,
                items: innerBlocks,
                content: innerContent,
                raw: `:::${hint}\n${innerContent}\n:::`,
            });
            i++;
            continue;
        }

        // Code block (```)
        const codeMatch = PATTERNS.codeBlockStart.exec(trimmed);
        if (codeMatch) {
            const language = codeMatch[1] || undefined;
            const codeLines: string[] = [];
            i++;

            while (i < lines.length && !PATTERNS.codeBlockEnd.test(lines[i].trim())) {
                codeLines.push(lines[i]);
                i++;
            }

            blocks.push({
                type: 'code',
                language,
                content: codeLines.join('\n'),
                raw: `\`\`\`${language || ''}\n${codeLines.join('\n')}\n\`\`\``,
            });
            i++;
            continue;
        }

        // Callout (> [!NOTE])
        const calloutMatch = PATTERNS.callout.exec(trimmed);
        if (calloutMatch) {
            const calloutType = calloutMatch[1].toLowerCase() as ParsedBlock['calloutType'];
            const contentLines: string[] = [];
            i++;

            // Collect callout content (lines starting with >)
            while (i < lines.length && PATTERNS.blockquote.test(lines[i].trim())) {
                const qMatch = PATTERNS.blockquote.exec(lines[i].trim());
                if (qMatch) contentLines.push(qMatch[1]);
                i++;
            }

            blocks.push({
                type: 'callout',
                calloutType,
                content: contentLines.join('\n'),
                inline: parseInline(contentLines.join(' ')),
                raw: `> [!${(calloutType || 'info').toUpperCase()}]\n${contentLines.map(l => `> ${l}`).join('\n')}`,
            });
            continue;
        }

        // Table
        if (PATTERNS.tableRow.test(trimmed)) {
            const rows: TableRow[] = [];
            let isFirstRow = true;

            while (i < lines.length && PATTERNS.tableRow.test(lines[i].trim())) {
                const rowLine = lines[i].trim();

                // Skip separator row
                if (PATTERNS.tableSeparator.test(rowLine)) {
                    i++;
                    continue;
                }

                const cells = rowLine
                    .slice(1, -1)
                    .split('|')
                    .map(cell => cell.trim());

                rows.push({ cells, isHeader: isFirstRow });
                isFirstRow = false;
                i++;
            }

            const hint = inferTableHint(rows);

            blocks.push({
                type: 'table',
                hint,
                rows,
                raw: rows.map(r => `| ${r.cells.join(' | ')} |`).join('\n'),
            });
            continue;
        }

        // Ordered list
        const orderedMatch = PATTERNS.orderedList.exec(trimmed);
        if (orderedMatch) {
            const items: ParsedBlock[] = [];

            while (i < lines.length) {
                const listLine = lines[i].trim();
                const olMatch = PATTERNS.orderedList.exec(listLine);

                if (!olMatch) break;

                items.push({
                    type: 'paragraph',
                    content: olMatch[2],
                    inline: parseInline(olMatch[2]),
                    raw: listLine,
                });
                i++;
            }

            blocks.push({
                type: 'list',
                ordered: true,
                hint: 'steps',
                items,
                raw: items.map((item, idx) => `${idx + 1}. ${item.content}`).join('\n'),
            });
            continue;
        }

        // Unordered list
        if (PATTERNS.unorderedList.test(trimmed)) {
            const items: ParsedBlock[] = [];

            while (i < lines.length) {
                const listLine = lines[i].trim();
                const ulMatch = PATTERNS.unorderedList.exec(listLine);

                if (!ulMatch) break;

                items.push({
                    type: 'paragraph',
                    content: ulMatch[1],
                    inline: parseInline(ulMatch[1]),
                    raw: listLine,
                });
                i++;
            }

            const hint = inferListHint(items);

            blocks.push({
                type: 'list',
                ordered: false,
                hint,
                items,
                raw: items.map(item => `- ${item.content}`).join('\n'),
            });
            continue;
        }

        // Blockquote (regular)
        const quoteMatch = PATTERNS.blockquote.exec(trimmed);
        if (quoteMatch) {
            const quoteLines: string[] = [];

            while (i < lines.length && PATTERNS.blockquote.test(lines[i].trim())) {
                const qMatch = PATTERNS.blockquote.exec(lines[i].trim());
                if (qMatch) quoteLines.push(qMatch[1]);
                i++;
            }

            blocks.push({
                type: 'blockquote',
                content: quoteLines.join('\n'),
                inline: parseInline(quoteLines.join(' ')),
                raw: quoteLines.map(l => `> ${l}`).join('\n'),
            });
            continue;
        }

        // Default: paragraph
        const emojiMatch = PATTERNS.emojiPrefix.exec(trimmed);
        blocks.push({
            type: 'paragraph',
            content: trimmed,
            inline: parseInline(trimmed),
            emojiPrefix: emojiMatch?.[1],
            raw: line,
        });
        i++;
    }

    return blocks;
}

// ============================================================================
// Semantic Inference
// ============================================================================

function inferTableHint(rows: TableRow[]): SemanticHint | undefined {
    if (rows.length === 0) return undefined;

    const headers = rows[0]?.cells.map(c => c.toLowerCase()) || [];

    // Comparison
    if (headers.includes('vs') || headers.some(h => h.includes('option') || h.includes('compare'))) {
        return 'comparison';
    }

    // Metrics
    if (headers.some(h => ['metric', 'value', 'score', 'rating', 'status', 'kpi'].includes(h))) {
        return 'metrics';
    }

    // Timeline
    if (headers.some(h => ['phase', 'week', 'month', 'step', 'stage', 'date', 'timeline', 'day'].includes(h))) {
        return 'timeline';
    }

    return undefined;
}

function inferListHint(items: ParsedBlock[]): SemanticHint | undefined {
    if (items.length === 0) return undefined;

    // Steps
    if (items.some(item => /^step\s*\d|^first|^then|^finally|^next|^\d+\./i.test(item.content || ''))) {
        return 'steps';
    }

    // Recommendations
    if (items.some(item => /recommend|suggest|consider|should|could|advise/i.test(item.content || ''))) {
        return 'recommendation';
    }

    // Warning indicators
    if (items.some(item => /caution|warning|avoid|don't|risk|danger/i.test(item.content || ''))) {
        return 'warning';
    }

    return undefined;
}

// ============================================================================
// Safe Parser (with fallback)
// ============================================================================

export function safeParseMarkdown(markdown: string): ParsedBlock[] {
    try {
        const blocks = parseMarkdown(markdown);

        if (blocks.length === 0 && markdown.trim()) {
            return [{
                type: 'paragraph',
                content: markdown,
                inline: parseInline(markdown),
                raw: markdown,
            }];
        }

        return blocks;
    } catch (error) {
        console.error('Parser error, falling back to raw text:', error);
        return [{
            type: 'paragraph',
            content: markdown,
            inline: [{ type: 'text', content: markdown }],
            raw: markdown,
        }];
    }
}

// ============================================================================
// Exports for backwards compatibility
// ============================================================================

export { parseInline as parseInlineV2 };
